import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './LegalApp.css';
import Navbar from './Navbar';
import Footer from './Footer';
import mammoth from "mammoth";
import { FaFileWord } from "react-icons/fa";
import { FaFilePdf } from "react-icons/fa";
import { FaFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";
import CreatableSelect from "react-select/creatable";
import { LEGACY_FONTS, convertLegacyToUnicode, isLegacyFontSelected } from "../utils/legacyFontConverter";

const UploadDocument = ({ API_URL }) => {
  const caseTypeOptions = [
    { value: "Abduction", label: "Abduction" },
    { value: "Cheating", label: "Cheating" },
    { value: "Copyright Act", label: "Copyright Violation" },
    { value: "Criminal Trespass", label: "Criminal Trespass" },
    { value: "Cybercrime", label: "Cybercrime" },
    { value: "Dacoity", label: "Dacoity" },
    {
      value: "Prevention of Damage to Public Property Act",
      label: "Damage to Public Property",
    },
    { value: "Dowry Death", label: "Dowry Death" },
    {
      value: "Explosive Substances Act",
      label: "Explosive Substances",
    },
    { value: "Extortion", label: "Extortion" },
    { value: "Gang Rape", label: "Gang Rape" },
    { value: "Grievous Injury", label: "Grievous Injury" },
    {
      value: "Illegal Arms Possession",
      label: "Illegal Arms Possession (Arms Act)",
    },
    {
      value: "Liquor Smuggling (Excise Act)",
      label: "Liquor Smuggling (Excise Act)",
    },
    { value: "Matrimonial", label: "Matrimonial" },
    { value: "Murder", label: "Murder" },
    { value: "Narcotics", label: "Narcotics (NDPS Act)" },
    { value: "POCSO", label: "POCSO" },
    { value: "Rape", label: "Rape" },
    { value: "Road Accident", label: "Road Accident" },
    { value: "Robbery", label: "Robbery" },
    { value: "Simple Injury", label: "Simple Injury" },
    { value: "Theft", label: "Theft" },
    { value: "Trade Marks Act", label: "Trade Mark Violation" },
    {
      value: "Unlawful Activities (UAPA)",
      label: "Unlawful Activities (UAPA)",
    },
  ];

  const [selectedUser, setSelectedUser] = useState("Investigation");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [pocCode, setPocCode] = useState('');
  const [users, setUsers] = useState([]);
  const [showOtherLanguagePopup, setShowOtherLanguagePopup] = useState(false);
  const [showCaseScopePopup, setShowCaseScopePopup] = useState(false);
  const [language, setLanguage] = useState("");
  const [caseType, setCaseType] = useState("");
  const [policeStation, setPoliceStation] = useState("");
  const [legacyFont, setLegacyFont] = useState(LEGACY_FONTS[0]);
  const [sectionsEntry, setSectionsEntry] = useState("");
  const [complainantName, setComplainantName] = useState("");
  const [accusedNamesEntry, setAccusedNamesEntry] = useState("");
  const fileInputRef = useRef(null);

  const pdfFileInputRef = useRef(null);
  const [parsingLabel, setParsingLabel] = useState("Reading document...");

  const hostname = window.location.hostname.toLowerCase();
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.backgroundColor = "white";

    // Set default user based on role
    if (localStorage.getItem('role') === 'admin') {
      setUsers(["Investigation", "Prosecution Counsel", "Defense Counsel", "Complainant", "Accused"]);
      setSelectedUser("Investigation");
    } else if (localStorage.getItem('role') === 'Advocate') {
      setUsers(["Prosecution Counsel", "Defense Counsel"]);
      setSelectedUser("Defense Counsel");
    } else if (localStorage.getItem('role') === 'Police') {
      setUsers(["Investigation"]);
      setSelectedUser("Investigation");
    } else {
      setUsers(["Complainant", "Accused"]);
      setSelectedUser("Accused");
    }
  }, []);

  const handleUserChange = (event) => {
    setSelectedUser(event.target.value);
  }

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const validateMandatoryFIRFields = (text) => {
    const full = (text || "").replace(/\r\n/g, "\n");
    const allLines = full.split("\n");

    const isLabelLine = (line, label) =>
      new RegExp(`^\\s*${label}\\s*:?\\s*(.*)$`, "i").test(line);

    const getFieldValueFromLines = (label, lines) => {
      const labelRegex = new RegExp(`^\\s*${label}\\s*:?\\s*(.*)$`, "i");

      for (let i = 0; i < lines.length; i++) {
        const m = lines[i].match(labelRegex);
        if (!m) continue;

        const sameLineValue = (m[1] || "").trim();
        if (sameLineValue) return sameLineValue;

        // If nothing after "Label" or "Label:", try next line IF it's not another label
        const next = (lines[i + 1] || "").trim();
        if (!next) return "";

        const nextIsAnotherLabel =
          isLabelLine(next, "Date") ||
          isLabelLine(next, "Sections") ||
          isLabelLine(next, "Complainant") ||
          isLabelLine(next, "Accused Person\\(s\\)") ||
          isLabelLine(next, "Complaint");

        return nextIsAnotherLabel ? "" : next;
      }

      return ""; // label not found at all
    };

    // Header = everything before Complaint:
    const header = full.split(/^\s*Complaint\s*:\s*$/im)[0] || "";
    const headerLines = header.split("\n");

    const date = getFieldValueFromLines("Date", headerLines);
    const sections = getFieldValueFromLines("Sections", headerLines);
    const complainant = getFieldValueFromLines("Complainant", headerLines);


    // Accused: parse lines after "Accused Person(s):" until end of header
    let accusedNames = [];
    for (let i = 0; i < headerLines.length; i++) {
      const accusedLineMatch = headerLines[i].match(
        /^\s*Accused Person\(s\)\s*:\s*(.*)$/i
      );
      if (!accusedLineMatch) continue;

      const collected = [];
      const sameLine = (accusedLineMatch[1] || "").trim();
      if (sameLine) collected.push(sameLine);

      for (let j = i + 1; j < headerLines.length; j++) {
        const t = (headerLines[j] || "").trim();
        if (!t) continue;

        // stop if we hit another header label
        const stop =
          isLabelLine(t, "Date") ||
          isLabelLine(t, "Sections") ||
          isLabelLine(t, "Complainant") ||
          isLabelLine(t, "Accused Person\\(s\\)") ||
          isLabelLine(t, "Complaint");
        if (stop) break;

        collected.push(t);
      }

      accusedNames = collected
        .map((l) => l.replace(/^\s*\d+\.\s*/, "").trim())
        .filter((l) => l.length > 0 && !/^\d+\.$/.test(l)); // ignore "1." placeholders

      break;
    }

    // Complaint: capture everything after "Complaint:" (same line + rest of document)
    let complaintText = "";
    for (let i = 0; i < allLines.length; i++) {
      const m = allLines[i].match(/^\s*Complaint\s*:\s*(.*)$/i);
      if (!m) continue;

      const sameLine = (m[1] || "").trim();

      // Everything after this line is part of complaint too
      const rest = allLines.slice(i + 1).join("\n").trim();

      complaintText = (sameLine ? sameLine + "\n" : "") + rest;
      complaintText = complaintText.trim();
      break;
    }

    if (!date) return "Please provide the FIR date.";
    if (!sections) return "Please provide the FIR sections.";
    if (!complainant) return "Please provide the complainant name, or mention Hidden.";
    if (accusedNames.length === 0)
      return "Please provide at least one accused person name, or mention Unknown.";
    if (!complaintText)
      return "Please provide the complaint.";

    return null;
  };

  const stripSectionsColonForSubmit = (text) => {
    const full = (text || "").replace(/\r\n/g, "\n");

    // Only removes ":" after the label "Sections" at the start of a line
    // "Sections: 420, 34" -> "Sections 420, 34"
    // "Sections:" -> "Sections"
    return full.replace(/^(\s*)Sections\s*:\s*(.*)$/gim, (_, indent, value) => {
      const v = (value || "").trim();
      return v ? `${indent}Sections ${v}` : `${indent}Sections`;
    });
  };

  const handlePopupSubmit = async () => {
    setShowPopup(false);
    setIsLoading(true);
    setErrorMessage(null);

    var voucher = pocCode;
    if (hostname === "i4c.legalapp.in") {
      voucher = "";
    }

    var jwt = localStorage.getItem('jwtToken');

    const formData = new FormData();
    formData.append('user', selectedUser);
    formData.append('pocCode', voucher);
    formData.append('language', language);
    formData.append('caseType', caseType);
    formData.append('policeStation', policeStation);
    formData.append('sections', sectionsEntry);
    formData.append('complainant', complainantName);
    formData.append('accusedList', accusedNamesEntry);

    if (isTyping) {
      //const firTextToSubmit = stripSectionsColonForSubmit(firText);
      //alert(firTextToSubmit);
      let textToSubmit;
      if (hostname !== "ai.legalapp.in") {
        //"FIR No. (optional):\nP.S. (optional):\nDate:\nSections:\nComplainant:\nAccused Person(s):\n1.\n2.\n3.\n\nComplaint:\n";
        textToSubmit = "Sections " + sectionsEntry + "\nComplainant: " + complainantName + "\nAccused Person(s): " + accusedNamesEntry + "\nComplaint:\n" + firText;
      } else {
        textToSubmit = firText;
      }

      if (transactionText) {
        textToSubmit += "\n\nTRANSACTION STATEMENT:\n" + transactionText + "\n";
      }

      formData.append('firText', textToSubmit);
    } else {
      formData.append('file', selectedFile);
    }

    try {
      // var uri = '/fir-pdf';
      // if (isTyping) {
      //   uri = '/fir';
      // }

      var uri = '/fir';
      const response = await axios.post(API_URL + uri, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${jwt}`,
        },
      });
      //console.log('API Response Status:', response.status);
      //console.log('API Response:', response.data);

      localStorage.setItem('user', selectedUser);
      if (isTyping) {
        navigate('/mycases', { state: { API_URL: API_URL } });
      } else {
        navigate('/fir/complaint', { state: { API_URL: API_URL, data: response.data, fromPage: 'Upload FIR' } });
      }
    } catch (error) {
      const defaultErrorMessage = 'An error occurred while submitting the form. Please try again later.';
      if (error.response?.status === 401) {
        setErrorMessage("Invalid voucher code. For assistance, please WhatsApp us at 9217224586.");
      } else {
        setErrorMessage(error.response?.data?.message || error.message || defaultErrorMessage);
      }
      console.error('Error submitting the form:', error);
    } finally {
      setIsLoading(false);
      localStorage.removeItem('firList');
    }
  };

  const handleLanguageChange = (event) => {
    if (event.target.value === 'Other') {
      setShowOtherLanguagePopup(true);
    } else {
      setLanguage(event.target.value);
      setShowOtherLanguagePopup(false);
    }
  };

  const handleCaseTypeChange = (event) => {
    setCaseType(event.target.value);
  };

  const handleLanguageChangePopup = (event) => {
    setShowOtherLanguagePopup(false);
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (isTyping) {
      if (caseType === "") {
        setErrorMessage("Select the case type.");
        return;
      }
      if (language === "") {
        setErrorMessage("Select the FIR language.");
        return;
      }
      if (sectionsEntry === "") {
        setErrorMessage("Enter the FIR sections.");
        return;
      }
      if (complainantName === "") {
        setErrorMessage("Enter the complainant name.");
        return;
      }
      if (accusedNamesEntry === "") {
        setErrorMessage("Enter the accused names. If the accused name is not known, enter Unknown.");
        return;
      }
      if (firText.trim() === "") {
        setErrorMessage("Enter the FIR contents.");
        return;
      }
      /*
      const validationError = validateMandatoryFIRFields(firText);
      if (validationError) {
        setErrorMessage(validationError);
        return;
      }
      */
    } else if (!selectedFile) {
      setErrorMessage("Please select the document to proceed.");
      return;
    }

    // if (hostname === "i4c.legalapp.in") {
    //   handlePopupSubmit();
    // } else {
    //   setShowPopup(true);
    // }

    handlePopupSubmit();
  };

  const handleCloseCaseScopePopup = () => {
    setShowCaseScopePopup(false);
    setShowPopup(true);
  }

  const DEFAULT_FIR_TEMPLATE =
    "FIR No. (optional):\nP.S. (optional):\nDate:\nSections:\nComplainant:\nAccused Person(s):\n1.\n2.\n3.\n\nComplaint:\n";

  const wordFileInputRef = useRef(null);

  const [isTyping, setIsTyping] = useState(true);
  //const [firText, setFirText] = useState('FIR No. (optional):\nP.S. (optional):\nDate:\nSections:\nComplainant:\nAccused Person(s):\n1.\n2.\n3.\n\nComplaint:\n');
  const [firText, setFirText] = useState('');
  // Holds the last raw (un-converted) complaint text - either straight from
  // the Word file or straight from the user's typing/pasting - so switching
  // the Legacy Font dropdown re-converts from that original source instead
  // of re-running a conversion on text that may already be converted once.
  const rawFirTextRef = useRef('');
  const [isParsingFile, setIsParsingFile] = useState(false);

  const MAX_WORD_COUNT = 1000;
  const countWords = (text) => {
    const trimmed = (text || '').trim();
    return trimmed ? trimmed.split(/\s+/).length : 0;
  };
  const firWordCount = countWords(firText);
  const isOverWordLimit = firWordCount > MAX_WORD_COUNT;
  const [showWordLimitPopup, setShowWordLimitPopup] = useState(false);
  const wasOverWordLimitRef = useRef(false);

  useEffect(() => {
    if (isOverWordLimit && !wasOverWordLimitRef.current) {
      setShowWordLimitPopup(true);
    }
    wasOverWordLimitRef.current = isOverWordLimit;
  }, [isOverWordLimit]);

  const upsertComplaintTemplateAndInsert = (currentText, fileContent) => {
    const template = DEFAULT_FIR_TEMPLATE;
    const content = (fileContent || "").trim();

    // Match "Complaint:" (case-insensitive), allowing spaces: "Complaint :"
    const complaintRegex = /Complaint\s*:\s*/i;

    let text = currentText || "";

    // If "Complaint:" missing, reset to default template
    if (!complaintRegex.test(text)) {
      text = template;
    }

    const match = complaintRegex.exec(text);
    if (!match) {
      // Shouldn't happen because we ensured it exists, but be safe
      return template + content + "\n";
    }

    const insertPos = match.index + match[0].length;

    // Replace everything AFTER "Complaint:" with the file content
    const before = text.slice(0, insertPos);
    return `${before}\n${content}\n`;
  };

  const handleWordFileSelected = async (event) => {
    setParsingLabel("Reading Word document...");

    try {
      const file = event.target.files?.[0];
      if (!file) return;

      const isDocx =
        file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        file.name.toLowerCase().endsWith(".docx");

      if (!isDocx) {
        setErrorMessage("Please select a .docx Word document.");
        return;
      }

      setIsParsingFile(true); // 🔹 START LOADER

      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      const extractedText = (result?.value || "").trim();

      if (!extractedText) {
        setErrorMessage("No readable text found in the selected Word document.");
        return;
      }

      // setFirText((prev) =>
      //   upsertComplaintTemplateAndInsert(prev, extractedText)
      // );
      rawFirTextRef.current = extractedText;
      setFirText(isLegacyFontSelected(legacyFont) ? convertLegacyToUnicode(extractedText, legacyFont) : extractedText);
      if (!isTyping) setIsTyping(true);
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to read the Word document. Please try another file.");
    } finally {
      setIsParsingFile(false); // 🔹 STOP LOADER
      if (wordFileInputRef.current) wordFileInputRef.current.value = "";
    }
  };

  // Converts pasted text on the way in when a legacy font is selected - the
  // dropdown-driven reconversion only fires on a font *change*, so without
  // this a paste (e.g. copying a paragraph out of an old Kruti Dev document)
  // after the font was already selected would silently stay unconverted.
  const handleFirTextPaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData).getData("text");
    if (!pasted) return;
    e.preventDefault();

    const textarea = e.target;
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;

    const convertedPasted = isLegacyFontSelected(legacyFont) ? convertLegacyToUnicode(pasted, legacyFont) : pasted;
    const newDisplayed = firText.slice(0, selectionStart) + convertedPasted + firText.slice(selectionEnd);

    // Best-effort: mirror the same splice into the raw baseline using the
    // original (un-converted) pasted text, so a later font switch still
    // reconverts from the true source. Exact when raw/displayed are still
    // aligned (nothing converted yet, or pasting at the end); approximate
    // otherwise, since conversion can change text length.
    const raw = rawFirTextRef.current;
    const rawStart = Math.min(selectionStart, raw.length);
    const rawEnd = Math.min(selectionEnd, raw.length);
    rawFirTextRef.current = raw.slice(0, rawStart) + pasted + raw.slice(rawEnd);

    setFirText(newDisplayed);

    const newCursorPos = selectionStart + convertedPasted.length;
    setTimeout(() => textarea.setSelectionRange(newCursorPos, newCursorPos), 0);
  };

  const handleWordFileClick = () => {
    setErrorMessage(null);
    wordFileInputRef.current?.click();
  };

  const extractBetweenLabels = (fullText) => {
    const text = (fullText || "")
      .replace(/\r\n/g, "\n")
      .replace(/\u00A0/g, " ")
      .replace(/[ \t]+/g, " ")
      .replace(/\n+/g, "\n")
      .trim();

    if (!text) return "";

    // Start label can be:
    // - "First Information contents"
    // - "FIR Contents"
    // - "F.I.R. Contents"
    // all case-insensitive, flexible whitespace/dots
    const startRe =
      /(first\s+information\s+contents|f\.?\s*i\.?\s*r\.?\s*contents)\s*/i;

    const endRe = /action\s+taken\s*/i;

    const startMatch = startRe.exec(text);
    if (!startMatch) return "";

    const afterStart = text.slice(startMatch.index + startMatch[0].length);

    const endMatch = endRe.exec(afterStart);
    if (!endMatch) return "";

    return afterStart.slice(0, endMatch.index).trim();
  };

  const handlePdfFileSelected = async (event) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      const isPdf =
        file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
      if (!isPdf) {
        setErrorMessage("Please select a PDF file.");
        return;
      }

      setErrorMessage(null);
      setIsParsingFile(true);
      setParsingLabel("Uploading PDF for OCR. Please wait...");

      // Call OCR API (server-side)
      const firTextFromOcr = await callPdfOcrApi(file, language);

      if (!firTextFromOcr || firTextFromOcr.trim().length === 0) {
        setErrorMessage("Could not read the FIR Contents.");
        return;
      }

      setParsingLabel("Extracting FIR contents...");
      const extracted = extractBetweenLabels(firTextFromOcr);

      if (!extracted) {
        setErrorMessage("Could not read the FIR Contents.");
        return;
      }

      //setFirText((prev) => upsertComplaintTemplateAndInsert(prev, extracted));
      setFirText(extracted);
      if (!isTyping) setIsTyping(true);
    } catch (err) {
      console.error(err);
      const apiMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message;

      setErrorMessage(apiMsg || "Failed to process the PDF. Please try again.");
    } finally {
      setIsParsingFile(false);
      if (pdfFileInputRef.current) pdfFileInputRef.current.value = "";
    }
  };

  const callPdfOcrApi = async (file, language) => {
    const jwt = localStorage.getItem("jwtToken");
    if (!jwt) throw new Error("Missing JWT token. Please login again.");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("language", language);

    const resp = await axios.post(`${API_URL}/fir/pdf/ocr`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${jwt}`,
      },
    });

    return resp?.data?.firText || "";
  };

  const handlePdfFileClick = () => {
    if (language === "") {
      setErrorMessage("Please select the language before uploading the document.");
      return;
    }

    setErrorMessage(null);
    setShowPdfConfirm(true);
    //pdfFileInputRef.current?.click();
  };

  const handlePdfConfirmCancel = () => {
    setShowPdfConfirm(false);
  };

  const handlePdfConfirmContinue = () => {
    setShowPdfConfirm(false);
    // open file picker AFTER modal closes
    setTimeout(() => pdfFileInputRef.current?.click(), 0);
  };


  const handleTyping = () => {
    setIsTyping(!isTyping);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const [showPdfConfirm, setShowPdfConfirm] = useState(false);

  const excelFileInputRef = useRef(null);
  const [transactionText, setTransactionText] = useState("");
  const [transactionFile, setTransactionFile] = useState(null);
  const isExcelSupportedCase = caseType === "Cheating" || caseType === "Cybercrime";
  useEffect(() => {
    if (!isExcelSupportedCase && transactionFile) {
      setTransactionFile(null);
      setTransactionText("");
    }
  }, [caseType]);

  const [isParsingTransaction, setIsParsingTransaction] = useState(false);

  const handleExcelFileClick = () => {
    setErrorMessage(null);
    excelFileInputRef.current?.click();
  };

  const handleExcelFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const ext = file.name.toLowerCase();

    if (!ext.endsWith(".xls") && !ext.endsWith(".xlsx")) {
      setErrorMessage("Please select an .xls or .xlsx file only.");
      return;
    }

    try {
      setErrorMessage(null);
      setIsParsingTransaction(true);
      setTransactionFile(file);

      const arrayBuffer = await file.arrayBuffer();

      const workbook = XLSX.read(arrayBuffer, {
        type: "array",
      });

      let finalText = "";

      workbook.SheetNames.forEach((sheetName) => {
        const worksheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
        });

        finalText += `\n=== SHEET: ${sheetName} ===\n`;

        jsonData.forEach((row) => {
          finalText += row.join(" | ") + "\n";
        });
      });

      setTransactionText(finalText);

    } catch (error) {
      console.error(error);

      setTransactionFile(null);
      setTransactionText("");

      setErrorMessage(
        error.message || "Failed to read Excel file."
      );
    } finally {
      setIsParsingTransaction(false);

      if (excelFileInputRef.current) {
        excelFileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveTransaction = () => {
    setTransactionFile(null);
    setTransactionText("");
  };


  return (
    <div>
      <Navbar />

      <div className="form-container" style={{ marginTop: '100px' }}>
        <div style={{ alignContent: 'center', textAlign: 'center' }}>
          <h2>Analyze FIR</h2>
        </div>
        <br></br>
        <form className="responsive-form" onSubmit={handleSubmit}>

          {/* <div className="form-group">
            <label htmlFor="document-type">Case Type</label>
            <select id="case-type" name="case-type" className="upload-select" onChange={handleCaseTypeChange} value={caseType}>
              <option value="" disabled>
                Select Case Type
              </option>

              <option value="Abduction">Abduction</option>
              <option value="Cheating">Cheating</option>
              <option value="Copyright Act">Copyright Violation</option>
              <option value="Criminal Trespass">Criminal Trespass</option>
              <option value="Cybercrime">Cybercrime</option>
              <option value="Dacoity">Dacoity</option>
              <option value="Prevention of Damage to Public Property Act">Damage to Public Property</option>
              <option value="Dowry Death">Dowry Death</option>
              <option value="Explosive Substances Act">Explosive Substances</option>
              <option value="Extortion">Extortion</option>
              <option value="Gang Rape">Gang Rape</option>
              <option value="Grievous Injury">Grievous Injury</option>
              <option value="Illegal Arms Possession">Illegal Arms Possession (Arms Act)</option>
              <option value="Liquor Smuggling (Excise Act)">Liquor Smuggling (Excise Act)</option>
              <option value="Matrimonial">Matrimonial</option>
              <option value="Murder">Murder</option>
              <option value="Narcotics">Narcotics (NDPS Act)</option>
              <option value="POCSO">POCSO</option>
              <option value="Rape">Rape</option>
              <option value="Road Accident">Road Accident</option>
              <option value="Robbery">Robbery</option>
              <option value="Simple Injury">Simple Injury</option>
              <option value="Theft">Theft</option>
              <option value="Trade Marks Act">Trade Mark Violation</option>
              <option value="Unlawful Activities (UAPA)">Unlawful Activities (UAPA)</option>
            </select>
          </div> */}
          <div className="form-group">
            <label htmlFor="case-type">Case Type</label>

            <CreatableSelect
              inputId="case-type"
              classNamePrefix="upload-select"
              options={caseTypeOptions}
              placeholder="Select or Enter Case Type"
              isClearable
              formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
              value={
                caseType
                  ? {
                    value: caseType,
                    label: caseType,
                  }
                  : null
              }
              onChange={(selectedOption) => {
                const value = selectedOption ? selectedOption.value : "";
                setCaseType(value);

                // Optional: keep your existing handler
                handleCaseTypeChange({
                  target: {
                    name: "case-type",
                    value,
                  },
                });
              }}
              styles={{
                paddingTop: "10px",
                // Keep the overall width fixed
                container: (provided) => ({
                  ...provided,
                  width: "100%", // or "100%" if inside a fixed-width parent
                  marginTop: "4px",
                }),
                
                // Reduce the overall height
                control: (provided) => ({
                  ...provided,
                  minHeight: "34px",
                  height: "38px",
                  fontSize: "14px",
                }),

                // Reduce vertical padding
                valueContainer: (provided) => ({
                  ...provided,
                  height: "38px",
                  padding: "0 8px",
                  fontSize: "16px",
                }),

                // Keep text from expanding the control width
                singleValue: (provided) => ({
                  ...provided,
                  maxWidth: "calc(100% - 10px)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }),

                // Input field styling
                input: (provided) => ({
                  ...provided,
                  margin: 0,
                  padding: 0,
                }),

                // Dropdown indicator
                indicatorsContainer: (provided) => ({
                  ...provided,
                  height: "34px",
                }),

                // Clear (X) button
                clearIndicator: (provided) => ({
                  ...provided,
                  padding: "4px",
                }),

                // Dropdown arrow
                dropdownIndicator: (provided) => ({
                  ...provided,
                  padding: "4px",
                }),
              }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="document-type">Complaint Language</label>
            <select
              id="document-type"
              name="document-type"
              className="upload-select"
              onChange={handleLanguageChange}
              value={language}
            >
              <option value="" disabled>
                Select Language
              </option>

              <option value="English">English</option>
              <option value="Hindi">Hindi</option>
              <option value="Gujarati">Gujarati</option>
              <option value="Marathi">Marathi</option>
              <option value="Bengali">Bengali</option>
              <option value="Odia">Odia</option>
              <option value="Telugu">Telugu</option>
              <option value="Tamil">Tamil</option>
              <option value="Kannada">Kannada</option>
              <option value="Malayalam">Malayalam</option>
              <option value="Punjabi">Punjabi</option>
              <option value="Urdu">Urdu</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="legacy-font">Legacy Font</label>
            <select
              id="legacy-font"
              name="legacy-font"
              className="upload-select"
              value={legacyFont}
              onChange={(e) => {
                const newFont = e.target.value;
                setLegacyFont(newFont);
                if (isLegacyFontSelected(newFont) && rawFirTextRef.current.trim() !== "") {
                  setFirText(convertLegacyToUnicode(rawFirTextRef.current, newFont));
                } else if (newFont === LEGACY_FONTS[0]) {
                  // Not Applicable - restore the original text as first pasted/extracted, undoing any conversion.
                  setFirText(rawFirTextRef.current);
                }
              }}
              style={{ width: "100%", boxSizing: "border-box", height: "38px", padding: "5px 10px", marginTop: "5px", fontSize: "16px", borderRadius: "4px", border: "1px solid #ccc" }}
            >
              {LEGACY_FONTS.map((font) => (
                <option key={font} value={font}>{font}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="sections">Sections</label>
            <input
              type="text"
              id="sections"
              name="sections"
              className="upload-input"
              value={sectionsEntry}
              onChange={(e) => setSectionsEntry(e.target.value)}
              placeholder="e.g. 318 BNS; 66(C) IT Act"
              style={{ width: "100%", boxSizing: "border-box", height: "38px", padding: "5px 10px", marginTop: "5px", fontSize: "16px", borderRadius: "4px", border: "1px solid #ccc" }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="complainant">Complainant Name</label>
            <input
              type="text"
              id="complainant"
              name="complainant"
              className="upload-input"
              value={complainantName}
              onChange={(e) => setComplainantName(e.target.value)}
              placeholder="Enter Name"
              style={{ width: "100%", boxSizing: "border-box", height: "38px", padding: "5px 10px", marginTop: "5px", fontSize: "16px", borderRadius: "4px", border: "1px solid #ccc" }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="accused">Accused Persons</label>
            <input
              type="text"
              id="accused"
              name="accused"
              className="upload-input"
              value={accusedNamesEntry}
              onChange={(e) => setAccusedNamesEntry(e.target.value)}
              placeholder="Enter Names (separate with ,)"
              style={{ width: "100%", boxSizing: "border-box", height: "38px", padding: "5px 10px", marginTop: "5px", fontSize: "16px", borderRadius: "4px", border: "1px solid #ccc" }}
            />
          </div>


          {/* {localStorage.getItem('role') !== 'Police' && (
            <div className="form-group">
              <label htmlFor="user">Analysis For</label>
              <select id="user" name="user" className="upload-select" onChange={handleUserChange} value={selectedUser}>
                {users.map((user, index) => (
                  <option key={index} value={user}>
                    {user}
                  </option>
                ))}
              </select>
            </div>
          )} */}

          {/* <div className="form-group">
            <label htmlFor="document">Upload or Type FIR</label>
            <div className="file-input-container">
              <input
                type="file"
                id="document"
                name="document"
                accept=".pdf"
                onChange={handleFileChange}
                className="file-input"
                ref={fileInputRef}
              />
              <input
                type="text"
                value={selectedFile ? selectedFile.name : ''}
                placeholder="Select PDF"
                readOnly
                className="file-name"
                style={{ fontSize: "12px", }}
              />
              <button
                type="button"
                onClick={() => {
                  setIsTyping(false);
                  document.getElementById('document').click()
                }}
                className="browse-button"
                style={{
                  width: "75px",
                }}
                disabled={isLoading || language === 'Other'}
              >
                Browse
              </button>
              <button
                type="button"
                onClick={handleTyping}
                className="browse-button"
                style={{
                  width: "75px",
                  backgroundColor: isTyping ? "#4caf50" : "#e0e0e0", // green when active, grey when not
                  color: isTyping ? "white" : "black",
                  border: "1px solid #ccc"
                }}
                disabled={isLoading || language === 'Other'}
              >
                Type
              </button>
            </div>
          </div> */}

          <div className="form-group" style={{ position: "relative", flex: "1 1 100%", width: "100%" }}>
            <label htmlFor="document">Complaint</label>

            <textarea
              value={firText}
              onChange={(e) => {
                setFirText(e.target.value);
                rawFirTextRef.current = e.target.value;
              }}
              onPaste={handleFirTextPaste}
              disabled={isParsingFile}
              style={{
                width: "100%",
                height: "300px",
                marginTop: "10px",
                fontSize: "16px",
                lineHeight: "1.5",
                resize: "vertical",
                opacity: isParsingFile ? 0.5 : 1,
                boxSizing: "border-box",
              }}
            />

            <p style={{ marginTop: "8px", fontSize: "13px", textAlign: "right", color: isOverWordLimit ? "#d32f2f" : "#666" }}>
              {firWordCount} / {MAX_WORD_COUNT} words
            </p>

            {isParsingFile && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 5,
                  borderRadius: "4px",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div className="spinner" />
                  <p style={{ marginTop: "8px", fontWeight: 600 }}>
                    {parsingLabel}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            {/* <button
              type="submit"
              className="submit-button"
              style={{ fontWeight: "bold" }}
              disabled={isLoading || language === 'Other' || (!isTyping && !selectedFile) || (isTyping && firText.trim() === '')}
              hidden={errorMessage}>
              {isLoading ? <div className="spinner"></div> : 'Submit'}
            </button> */}

            {/* Hidden Word file input */}
            <input
              ref={wordFileInputRef}
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              style={{ display: "none" }}
              onChange={handleWordFileSelected}
            />

            <input
              ref={pdfFileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              style={{ display: "none" }}
              onChange={handlePdfFileSelected}
            />

            <input
              ref={excelFileInputRef}
              type="file"
              accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleExcelFileSelected}
              disabled={isLoading || isParsingTransaction}
              style={{ display: "none" }}
            />

            <div style={{ width: "100%" }}>
              {/* Left (Word/PDF) + Right (Submit) */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: "12px",
                }}
              >
                {/* LEFT group */}
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <button
                    type="button"
                    onClick={handleWordFileClick}
                    disabled={isLoading}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 14px",
                      borderRadius: "6px",
                      border: "1px solid #ccc",
                      background: "white",
                      cursor:
                        isLoading?
                        "not-allowed" : "pointer",
                      opacity:
                        transactionFile || !isExcelSupportedCase
                          ? 0.6
                          : 1,
                      fontWeight: 600,
                    }}
                    title="Upload a Word (.docx) file and insert its contents under Complaint:"
                  >
                    <FaFileWord style={{ fontSize: "18px", color: "#0207f9" }} />
                    Word
                  </button>

                  <button
                    type="button"
                    onClick={handlePdfFileClick}
                    disabled={isLoading}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 14px",
                      borderRadius: "6px",
                      border: "1px solid #ccc",
                      background: "white",
                      cursor:
                        isLoading || isParsingFile
                          ? "not-allowed"
                          : "pointer",
                      fontWeight: 600,
                    }}
                    title="Upload a PDF and insert Paragraph No. 12 under Complaint:"
                  >
                    <FaFilePdf style={{ fontSize: "18px", color: "#D32F2F" }} />
                    PDF
                  </button>

                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <button
                      type="button"
                      onClick={handleExcelFileClick}
                      disabled={
                        isLoading ||
                        isParsingTransaction ||
                        !!transactionFile ||
                        !isExcelSupportedCase
                      }
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "10px 14px",
                        borderRadius: "6px",
                        border: "1px solid #ccc",
                        background: "white",
                        cursor:
                          isLoading || isParsingTransaction
                            ? "not-allowed"
                            : "pointer",
                        fontWeight: 600,
                      }}
                      title="Upload an Excel account statement"
                    >
                      <FaFileExcel style={{ fontSize: "18px", color: "#217346" }} />
                      Excel
                    </button>

                    {transactionFile && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "14px",
                          background: "#f5f5f5",
                          padding: "6px 10px",
                          borderRadius: "6px",
                          border: "1px solid #ddd",
                        }}
                      >
                        <span>{transactionFile.name}</span>

                        <button
                          type="button"
                          onClick={handleRemoveTransaction}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "red",
                            cursor: "pointer",
                            fontWeight: "bold",
                            fontSize: "16px",
                            lineHeight: 1,
                          }}
                          title="Remove file"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* RIGHT Submit */}
                <button
                  type="submit"
                  className="submit-button"
                  style={{ fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center", height: "38px" }}
                  disabled={
                    isLoading ||
                    isParsingFile ||
                    isOverWordLimit
                  }
                  hidden={errorMessage}
                >
                  {isLoading ? <div className="spinner" style={{ width: "16px", height: "16px", borderWidth: "2px", margin: "0" }}></div> : "Submit"}
                </button>
              </div>

              <div
                hidden={!isLoading}
                style={{ marginTop: "10px", alignContent: "center", textAlign: "center" }}
              >
                <p style={{ fontSize: "14px" }} hidden={!isLoading}>
                  <strong>
                    Please wait. AI Agent is analyzing FIR - this may take about a minute
                    depending on the complexity of the complaint.
                  </strong>
                </p>
              </div>
            </div>

          </div>

          {/* <div hidden={isLoading || errorMessage}>
            <p style={{ textAlign: 'center', fontSize: "16px", }}>
              <strong>
                Upload FIR for instant analysis under Indian law
              </strong>
            </p>
            <ul style={{ listStyleType: 'disc', textAlign: 'left', padding: '0 20px' }}>
              <li style={{ marginBottom: '10px', fontSize: "16px", }}>
                Understand the core issues in the FIR with a clear breakdown of the allegations.
              </li>
              <li style={{ marginBottom: '10px', fontSize: "16px", }}>
                Know the applicable rights and remedies, ensuring informed decision-making.
              </li>
              <li style={{ marginBottom: '10px', fontSize: "16px", }}>
                Identify crucial points in the FIR to seek a fair and proper police investigation.
              </li>
              <li style={{ marginBottom: '10px', fontSize: "16px", }}>
                Identify crucial evidence (documents, witnesses) for proving or defending the FIR.
              </li>
              <li style={{ marginBottom: '10px', fontSize: "16px", }}>
                Access actionable strategies to expedite case conclusion and achieve timely justice.
              </li>
            </ul>
          </div> */}

        </form>

        {showPdfConfirm && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h3>PDF Upload</h3>
              <p style={{ textAlign: "center" }}>
                Max 4 pages only. Only computer-typed PDF documents can be read. Handwritten or improperly scanned PDFs may not work.
              </p>

              <div className="popup-buttons">
                <button className="cancel-button" onClick={handlePdfConfirmCancel}>
                  Cancel
                </button>
                <button className="submit-popup-button" onClick={handlePdfConfirmContinue}>
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h2>Error</h2>
              <div className="error-message" style={{ marginBottom: '15px', fontSize: '16px', color: '#333' }}>
                <strong>{errorMessage}</strong>
              </div>
              <button
                className="error-ok-button"
                onClick={() => setErrorMessage(null)}
                style={{ padding: '10px 20px', fontSize: '14px', color: 'white', backgroundColor: '#007bff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                OK
              </button>
            </div>
          </div>

        )}

        {showPopup && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h2>FIR Analysis Voucher Code</h2>
              <input
                value={pocCode}
                onChange={(e) => setPocCode(e.target.value)}
                className="poc-input"
                placeholder="Enter the voucher code."
                onKeyDown={(e) => {
                  if (e.key === "Enter" && pocCode.length >= 4) {
                    e.preventDefault(); // Prevent default behavior of form submission if needed
                    handlePopupSubmit();
                  }
                }}
              />
              <div className="popup-buttons">
                <button
                  className="cancel-button"
                  onClick={() => setShowPopup(false)}
                >Cancel</button>
                <button
                  className="submit-popup-button"
                  onClick={handlePopupSubmit}
                  disabled={pocCode.length < 4}
                >Submit</button>
              </div>
              {/* <h5>Please email contact@legalapp.in to get the analysis voucher.</h5> */}
            </div>
          </div>
        )}

        {showOtherLanguagePopup && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h3>Support for more languages coming soon!</h3>
              <p>In the meantime, please translate your FIR into English using tools like <a href="https://chat.openai.com/" target="_blank" rel="noopener noreferrer">ChatGPT</a> and <a href="https://openpdf.com/lp/translate-pdf.html" target="_blank" rel="noopener noreferrer">OpenPDF</a> and upload the translated FIR as a PDF.</p>
              <p><strong>For assistance, please email us at contact@legalapp.in.</strong></p>
              <div className="popup-buttons">
                <button
                  className="submit-popup-button"
                  onClick={handleLanguageChangePopup}
                >Close</button>
              </div>
            </div>
          </div>
        )}

        {showWordLimitPopup && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h3>Word Limit Reached</h3>
              <p style={{ textAlign: "center" }}>
                The free version supports a complaint of up to {MAX_WORD_COUNT} words. Please shorten the text to continue, or contact us for a paid plan with a higher limit.
              </p>
              <div className="popup-buttons">
                <button
                  className="submit-popup-button"
                  onClick={() => setShowWordLimitPopup(false)}
                >OK</button>
              </div>
            </div>
          </div>
        )}

        {/* {showCaseScopePopup && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h3>Current Scope of FIR Analysis</h3>
              <p style={{ textAlign: 'justify' }}>The app analyzes matrimonial police complaints only, involving: <strong>demanding or taking dowry</strong> (Sections 3 & 4, Dowry Prohibition Act), <strong>cruelty</strong> (IPC Section 498A / BNS Section 85), <strong>physical harm</strong> (IPC Sections 323, 325 / BNS Sections 115, 117), <strong>criminal breach of trust</strong> (IPC Section 406 / BNS Section 316), <strong>criminal intimidation</strong> (IPC Section 506 / BNS Section 351), and their related attempts and abetments.</p>
              <p style={{ textAlign: 'justify' }}>Support for more offenses is coming soon.</p>
              <div className="popup-buttons">
                <button
                  className="submit-popup-button"
                  onClick={handleCloseCaseScopePopup}
                >Continue</button>
              </div>
            </div>
          </div>
        )} */}
      </div>
      <Footer />
    </div>
  );
};

export default UploadDocument;
